import * as faceapi from "@vladmandic/face-api";
import { createEscalationEngine } from "./escalationEngine";
import { registerStrike, resetStrikes } from "./violationStrikeCounter";

export let _referenceFaceDescriptor = null;
export let _lastFaceMatchTime = 0;
let _modelsReady = false; // guard: skip inference until all nets are loaded
let _consecutiveFailures = 0; // self-heal counter, see checkPersonSwitch's catch block
let _modelLoadPromise = null;
let _lastLoadAttempt = 0;

const MATCH_DISTANCE_THRESHOLD = 0.65;
const SAMPLE_THROTTLE_MS = 500;
const MAX_CONSECUTIVE_FAILURES = 5; // ~2.5s at SAMPLE_THROTTLE_MS before forcing a re-init
const MODEL_LOAD_RETRY_COOLDOWN_MS = 2000;

// Configurable escalation timing -- see escalationEngine.js for the
// confirm/monitor workflow, and violationStrikeCounter.js for the shared
// warn/terminate decision (combined across Multiple-Person + Person-Switch).
const CONFIRMATION_WINDOW_MS = 2000; // initial check before logging the violation
const MONITOR_WINDOW_MS = 10000; // post-log monitoring before the popup fires
const VIOLATION_RATIO_THRESHOLD = 0.6;

const engine = createEscalationEngine({
  confirmationWindowMs: CONFIRMATION_WINDOW_MS,
  monitorWindowMs: MONITOR_WINDOW_MS,
  violationRatioThreshold: VIOLATION_RATIO_THRESHOLD,
});

export const personSwitchState = {
  lastFaceVerificationStatus: null,
};

export const personSwitchGetters = {};

export const personSwitchMutations = {
  SET_LAST_FACE_VERIFICATION_STATUS(state, status) {
    state.lastFaceVerificationStatus = status;
  },
};

// Shared by the normal init action and the self-heal path below, so a
// mid-exam recovery reloads models/reference exactly the same way startup
// does, instead of duplicating the logic.
async function loadModels() {
  _modelsReady = false; // reset in case of re-init
  try {
    const MODEL_URL = "/models";
    if (!faceapi.nets.tinyFaceDetector.isLoaded) {
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
    }
    if (!faceapi.nets.faceLandmark68Net.isLoaded) {
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
    }
    if (!faceapi.nets.faceRecognitionNet.isLoaded) {
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
    }

    const storedImage = localStorage.getItem("verified_user_image");

    if (storedImage) {
      const img = new Image();
      img.src = storedImage;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });

      let referenceSource = img;
      if (img.naturalWidth && img.naturalHeight) {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        referenceSource = canvas;
      }

      const detection = await faceapi
        .detectSingleFace(
          referenceSource,
          new faceapi.TinyFaceDetectorOptions()
        )
        .withFaceLandmarks()
        .withFaceDescriptor();
      if (detection) {
        _referenceFaceDescriptor = detection.descriptor;
        engine.reset();
        console.log("[Proctoring] Local face reference loaded.");
      } else {
        console.log(
          "[Proctoring] Could not detect face in stored reference image."
        );
      }
    } else {
      console.log("[Proctoring] No verified_user_image found in localStorage.");
    }

    // ✅ Only mark ready AFTER all nets are fully loaded
    _modelsReady = true;
    _consecutiveFailures = 0;
    console.log("[Proctoring] TinyFaceDetector models ready.");
  } catch (fErr) {
    console.log("[Proctoring] Failed to load face recognition models:", fErr);
  }
}

// concurrent second one.
function startOrJoinLoad() {
  if (!_modelLoadPromise) {
    _lastLoadAttempt = Date.now();
    _modelLoadPromise = loadModels().finally(() => {
      _modelLoadPromise = null;
    });
  }
  return _modelLoadPromise;
}

async function ensureModelsLoaded() {
  if (_modelsReady) return;
  if (_modelLoadPromise) {
    await _modelLoadPromise;
    return;
  }
  const now = Date.now();
  if (now - _lastLoadAttempt < MODEL_LOAD_RETRY_COOLDOWN_MS) return;
  await startOrJoinLoad();
}

export const personSwitchActions = {
  async initPersonSwitchModels() {
    await ensureModelsLoaded();
  },

  async checkPersonSwitch({ dispatch }, { videoEl }) {
    await ensureModelsLoaded();
    if (!_modelsReady) return;

    const now = Date.now();
    if (now - _lastFaceMatchTime < SAMPLE_THROTTLE_MS) return;
    _lastFaceMatchTime = now;

    try {
      const matchDetections = await faceapi
        .detectAllFaces(videoEl, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptors();

      const frameArea = (videoEl.videoWidth || 1) * (videoEl.videoHeight || 1);
      const MIN_FACE_AREA_RATIO = 0.03;
      const significantDetections = (matchDetections || []).filter((d) => {
        const { width, height } = d.detection.box;
        return (width * height) / frameArea >= MIN_FACE_AREA_RATIO;
      });

      if (!_referenceFaceDescriptor || significantDetections.length === 0) {
        // Inconclusive frame — no reference yet, or no face this cycle
        // (occlusion, momentary "no face", lighting, head turned out of
        // frame, etc). Absence of evidence is not evidence of recovery, so
        // the escalation engine is left completely untouched: it neither
        // advances nor resets. cameraProctoring.js's own "No face detected"
        // violation handles sustained face-absence separately.
        return;
      }

      // Match against the PRIMARY face (largest bounding box = closest to
      // camera = the person actually at the keyboard), not "any face in
      // frame that happens to match." Checking every face for the closest
      // match let an impostor sit front-and-center while the enrolled
      // student's face was only marginally/peripherally visible (or a
      // propped-up photo) -- that smaller face matching was enough to mark
      // the whole frame "authorized" even though the prominent, actual
      // exam-taker was someone else. Multiple-person detection (a separate,
      // untouched module) still independently flags "more than one face."
      const primaryDetection = significantDetections.reduce((largest, d) => {
        const area = d.detection.box.width * d.detection.box.height;
        const largestArea =
          largest.detection.box.width * largest.detection.box.height;
        return area > largestArea ? d : largest;
      });
      const primaryDist = faceapi.euclideanDistance(
        _referenceFaceDescriptor,
        primaryDetection.descriptor
      );
      const matched = primaryDist <= MATCH_DISTANCE_THRESHOLD;

      const result = engine.evaluate(now, !matched, {
        distance: Number(primaryDist.toFixed(3)),
        faceCount: significantDetections.length,
      });

      _consecutiveFailures = 0; // a clean pass through detection -- reset the self-heal counter

      switch (result.action) {
        case "confirmed":
          // Initial 2s check held -- post the violation log now. No popup
          // yet: that only fires if the SAME episode is still violating
          // after the full 10s monitoring window below.
          dispatch("triggerViolation", {
            reason: "Person switch was detected",
            meta: result.evidence,
          });
          break;

        case "escalate": {
          // Still violating after the 10s monitor window -- this is the
          // popup/terminate decision point, shared across Multiple-Person +
          // Person-Switch: 2 combined strikes (any mix of types) warn, the
          // 3rd terminates.
          const strike = registerStrike("person-switch", result.evidence);
          if (strike.action === "terminate") {
            dispatch("triggerViolation", {
              reason: "Person switch confirmed — session closed",
              isLog: true,
              meta: { ...result.evidence, strikeLog: strike.strikeLog },
            });
            window.dispatchEvent(new CustomEvent("person-switch-alert"));
            dispatch("stopDetection");
          } else {
            window.dispatchEvent(
              new CustomEvent("person-switch-warning", {
                detail: { strikeNumber: strike.strikeNumber },
              })
            );
          }
          break;
        }

        case "resolved":
          window.dispatchEvent(new CustomEvent("person-switch-resolved"));
          break;

        default:
          break;
      }
    } catch (mErr) {
      _consecutiveFailures++;
      console.log(
        `[Proctoring] Face match check failed (${_consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`,
        mErr
      );
      // A single failure is usually a dropped frame -- harmless. But if
      // detectAllFaces keeps throwing (WebGL context loss, TF.js backend
      // crash, a stuck video frame), the whole check was previously dead
      // for the rest of the exam with nothing but a repeating warning as
      // evidence. Self-heal instead: reload the models/reference the same
      // way startup does, which also re-acquires a fresh backend context.
      if (_consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        _consecutiveFailures = 0;
        console.log(
          "[Proctoring] Too many consecutive face-match failures — reinitializing models."
        );
        startOrJoinLoad();
      }
    }
  },

  // Suspends the in-progress confirm/monitor window while Paper/Pen mode is
  // active (looking down at the page is expected, not evidence either way)
  // and resumes it afterwards -- via pause/resume rather than reset(), so
  // an already-confirmed "monitoring" episode (a real identity mismatch
  // that was already logged) can't be wiped out, and its strike dodged,
  // just by toggling Paper/Pen mode.
  pausePersonSwitchWindow() {
    engine.pause();
  },

  resumePersonSwitchWindow() {
    engine.resume();
  },

  cleanupPersonSwitch() {
    engine.reset();
    resetStrikes();
    _modelsReady = false; // reset so re-init works correctly
    _modelLoadPromise = null;
    _lastLoadAttempt = 0;
  },
};
